// Turns a Holiday Club registration form response into a HolidayRegistrationForm.
//
// Pure and TS 4.9 compatible: bundled into the invoice-generator Apps Script (HolidaySync.ts), which reads the
// form with FormApp and hands plain values to these functions, and unit-tested here.

import type { DayChoice, HolidayDay, HolidayRegistrationForm } from "./holidayInvoicing";

const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];

const pad = (n: number) => String(n).padStart(2, "0");

const WEEKDAYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
const DAY_MS = 24 * 60 * 60 * 1000;

interface LabelParts { weekday: number | null; day: number | null; month: number | null }

/** Weekday, day and month from "Mon 26 Oct", "[Monday 31 July]", "Monday 3rd April", "Thu 31". */
const labelParts = (label: string): LabelParts => {
  const words = label.replace(/[[\]\t(),]/g, " ").trim().split(/\s+/);
  const parts: LabelParts = { weekday: null, day: null, month: null };
  for (const word of words) {
    const lower = word.toLowerCase();
    if (parts.weekday === null && WEEKDAYS.indexOf(lower.slice(0, 3)) >= 0 && /^[a-z]+$/.test(lower)) {
      parts.weekday = WEEKDAYS.indexOf(lower.slice(0, 3));
    }
    const day = /^(\d{1,2})(st|nd|rd|th)?$/.exec(lower);
    if (parts.day === null && day) parts.day = parseInt(day[1], 10);
    const month = MONTHS.indexOf(lower.slice(0, 3));
    if (month >= 0 && lower.length >= 3 && /^[a-z]+$/.test(lower)) parts.month = month;
  }
  return parts;
};

/**
 * The date of a day and month: the form never says the year, so it is the first year in which the date falls no
 * earlier than a month before the form was created (a form made in December for February dates gets next year).
 */
const dateFor = (day: number, month: number, formCreatedAt: Date): string | null => {
  if (day < 1 || day > 31) return null;
  const earliest = new Date(formCreatedAt.getTime() - 31 * DAY_MS);
  for (let year = earliest.getUTCFullYear(); year <= earliest.getUTCFullYear() + 1; year++) {
    const date: Date = new Date(Date.UTC(year, month, day));
    if (date.getUTCMonth() !== month) return null; // e.g. 31 Nov
    if (date.getTime() >= Date.UTC(earliest.getUTCFullYear(), earliest.getUTCMonth(), earliest.getUTCDate())) {
      return `${year}-${pad(month + 1)}-${pad(day)}`;
    }
  }
  return null;
};

const weekdayOfIso = (iso: string) => new Date(`${iso}T12:00:00Z`).getUTCDay();
const timeOf = (iso: string) => Date.parse(`${iso}T12:00:00Z`);

/** One label on its own (see parseDayLabels for a whole booking grid). */
export const parseDayLabel = (label: string, formCreatedAt: Date): string | null => parseDayLabels([label], formCreatedAt)[0];

/**
 * Dates of a booking grid's row labels, read together so typos on the form are corrected from the other days:
 * ordinals ("3rd April") are understood, a label without a month ("Thu 31") takes its neighbours' month, and a day
 * far from the rest ("Thu 29 Jul" among October days) moves to a neighbouring day's month when its day number and
 * weekday fit there. null where nothing sensible can be read.
 */
export const parseDayLabels = (labels: string[], formCreatedAt: Date): (string | null)[] => {
  const parts = labels.map(labelParts);
  const months = parts.map((p, i) => {
    if (p.month !== null) return p.month;
    for (let d = 1; d < parts.length; d++) {
      const before = parts[i - d] && parts[i - d].month;
      if (before !== undefined && before !== null) return before;
      const after = parts[i + d] && parts[i + d].month;
      if (after !== undefined && after !== null) return after;
    }
    return null;
  });
  const dates = parts.map((p, i) => (p.day !== null && months[i] !== null ? dateFor(p.day, months[i]!, formCreatedAt) : null));

  const known = dates.filter((d): d is string => !!d).map(timeOf).sort((a, b) => a - b);
  if (known.length < 2) return dates;
  const median = known[Math.floor(known.length / 2)];
  const near = (iso: string) => Math.abs(timeOf(iso) - median) <= 21 * DAY_MS;
  return dates.map((date, i) => {
    if (!date || near(date)) return date;
    const p = parts[i];
    const candidates = dates.filter((d, j) => j !== i && d && near(d)).map((d) => parseInt(d!.slice(5, 7), 10) - 1);
    for (const month of candidates.filter((m, k) => candidates.indexOf(m) === k)) {
      const alternative = dateFor(p.day!, month, formCreatedAt);
      if (alternative && near(alternative) && (p.weekday === null || weekdayOfIso(alternative) === p.weekday)) return alternative;
    }
    return date;
  });
};

/** Maps a grid cell ("Full day (£35.00)", "Half day (£25.00)", "Not coming", or blank) to a choice. */
export const parseDayChoice = (answer: string | null | undefined): DayChoice => {
  const text = (answer || "").trim().toLowerCase();
  if (text.startsWith("full day")) return "FULL";
  if (text.startsWith("half day")) return "HALF";
  return "NONE";
};

/** Question titles as they appear on the forms; matched case-insensitively by prefix so small edits survive. */
export const QUESTION_PREFIXES = {
  childName: "child's full name",
  parentName: "parent/guardian full name",
  address: "home address",
  email: "email address",
  phone: "phone number",
} as const;

export type QuestionKey = keyof typeof QUESTION_PREFIXES;

/** Which question key a form item title belongs to, if any. */
export const questionKeyFor = (title: string): QuestionKey | null => {
  const normalised = title.trim().toLowerCase().replace(/[‘’]/g, "'");
  for (const key of Object.keys(QUESTION_PREFIXES) as QuestionKey[]) {
    if (normalised.startsWith(QUESTION_PREFIXES[key])) return key;
  }
  return null;
};

export interface RawResponse {
  submittedAt: Date;
  /** Text answers by question title. */
  answers: { title: string; answer: string }[];
  /** The booking grid: one answer per row label, null when left blank. */
  grid: { label: string; answer: string | null }[];
}

export const toRegistrationForm = (response: RawResponse, formCreatedAt: Date): HolidayRegistrationForm => {
  const values: { [key in QuestionKey]: string } = { childName: "", parentName: "", address: "", email: "", phone: "" };
  for (const { title, answer } of response.answers) {
    const key = questionKeyFor(title);
    if (key && !values[key]) values[key] = String(answer || "").trim();
  }
  const dates = parseDayLabels(response.grid.map(({ label }) => label), formCreatedAt);
  const days: HolidayDay[] = response.grid.map(({ label, answer }, i) => ({
    label: label.trim(),
    date: dates[i],
    choice: parseDayChoice(answer),
  }));
  return {
    submittedAt: response.submittedAt.toISOString(),
    childName: values.childName,
    parentName: values.parentName,
    address: values.address,
    email: values.email.trim(),
    phone: values.phone,
    days,
  };
};

/**
 * holidayTermDates/{termId}: the manager's corrections of a term's day dates, by row label. Kept apart from
 * holidayTerms so the form sync never overwrites them.
 */
export interface HolidayTermDates {
  dates: { [label: string]: string };
}

/**
 * The dates of a term's days as the portal shows them: the manager's correction where there is one, otherwise the
 * labels read again with parseDayLabels (so parser improvements apply without a re-sync).
 */
export const termDayDates = (
  term: { days: { label: string; date: string | null }[]; formCreatedAt: string },
  corrections: { [label: string]: string } = {}
): { label: string; date: string | null; parsed: string | null; corrected: boolean }[] => {
  const parsed = parseDayLabels(term.days.map((d) => d.label), new Date(term.formCreatedAt));
  return term.days.map((day, i) => ({
    label: day.label,
    parsed: parsed[i],
    date: corrections[day.label] || parsed[i],
    corrected: !!corrections[day.label] && corrections[day.label] !== parsed[i],
  }));
};
